import { usePermissions } from '@/hooks/usePermissions'
import { captureException } from '@sentry/nextjs'

import { useApolloClient } from '@apollo/client'

import { useUpdateDeviceMutation } from '@/graphql/generated/hooks'
import { UpdateDeviceInput } from '@/graphql/generated/schemas'

import { DeviceI } from '../types/types'

const REFETCH_QUERIES = ['FloorDevicesCount']
const REMOVE_REFETCH_QUERIES = ['FloorDevicesCount', 'FacilityDevicesByFloors']

export const useUpdateDevice = (floorId: string) => {
  const { shouldEnableUpdateDevice } = usePermissions()
  const client = useApolloClient()

  const [update, { error }] = useUpdateDeviceMutation()

  const buildUpdateInput = (newData: DeviceI): UpdateDeviceInput => ({
    id: newData.id,
    x: newData.x,
    y: newData.y,
    bearing: newData.rotation,
    fieldOfView: newData.angle,
    visibilityRange: newData.distance,
    floorId: floorId,
  })

  const updateDevice = async (newData: DeviceI): Promise<boolean> => {
    if (!shouldEnableUpdateDevice) return false

    await update({
      variables: { input: buildUpdateInput(newData) },
      refetchQueries: REFETCH_QUERIES,
    })
    return true
  }

  /**
   * Removes only the device's placement. Keeping this as a separate operation
   * prevents an invalid drag from being translated into nullable coordinates.
   */
  const removeDevicePlacement = async (deviceId: string): Promise<boolean> => {
    if (!shouldEnableUpdateDevice) return false

    await update({
      variables: { input: { id: deviceId, x: null, y: null } },
      refetchQueries: REMOVE_REFETCH_QUERIES,
    })
    return true
  }

  /**
   * Persists positions for several devices at once. There is no bulk device mutation, so this
   * fires one `UpdateDevice` per device.
   *
   * Refetching happens once after every write settles rather than per mutation, since mutations
   * do not necessarily resolve in the order issued and a mid-flight refetch would read a short
   * count.
   *
   * Never rejects — callers are not expected to await it. Resolves `true` when at least one
   * device failed to persist.
   */
  const updateDevicePositions = async (
    newDataList: DeviceI[]
  ): Promise<boolean> => {
    if (!shouldEnableUpdateDevice || newDataList.length === 0) return false

    const results = await Promise.allSettled(
      newDataList.map((newData) =>
        update({ variables: { input: buildUpdateInput(newData) } })
      )
    )

    results.forEach((result) => {
      if (result.status === 'rejected') {
        captureException(result.reason)
      }
    })

    if (results.some((result) => result.status === 'fulfilled')) {
      try {
        await client.refetchQueries({ include: REFETCH_QUERIES })
      } catch (e) {
        captureException(e)
      }
    }

    return results.some((result) => result.status === 'rejected')
  }

  return {
    error,
    removeDevicePlacement,
    updateDevice,
    updateDevicePositions,
  }
}
